import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Disc3, DollarSign, Users2 } from "lucide-react";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RosterDetail({ type }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/roster/${type}/${id}`).then((r) => setData(r.data)).catch(() => nav("/managed"));
  }, [type, id, nav]);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const { entity, releases = [], royalties = [], totals = {}, artists = [] } = data;

  return (
    <div className="space-y-6" data-testid="roster-detail-page">
      <Button variant="ghost" onClick={() => nav("/managed")} data-testid="roster-back"><ArrowLeft className="mr-2 h-4 w-4" />Back to My Roster</Button>
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{entity.type}</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">{entity.name}</h1>
        {entity.bio && <p className="mt-1 text-muted-foreground">{entity.bio}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-5" data-testid="roster-stat-releases"><div className="flex items-center gap-2 text-primary"><Disc3 className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Releases</p></div><p className="mt-2 text-3xl font-bold font-heading">{totals.releases ?? releases.length}</p></Card>
        <Card className="p-5" data-testid="roster-stat-payable"><div className="flex items-center gap-2 text-emerald-400"><DollarSign className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Total Payable</p></div><p className="mt-2 text-3xl font-bold font-heading">{money(totals.payable)}</p></Card>
        {entity.type === "label" && <Card className="p-5" data-testid="roster-stat-artists"><div className="flex items-center gap-2 text-blue-400"><Users2 className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Artists</p></div><p className="mt-2 text-3xl font-bold font-heading">{totals.artists ?? artists.length}</p></Card>}
      </div>

      {entity.type === "label" && artists.length > 0 && (
        <Card className="p-5" data-testid="roster-artists-card">
          <h3 className="mb-3 font-heading text-lg font-semibold">Artists</h3>
          <div className="flex flex-wrap gap-2">
            {artists.map((a) => (
              <Button key={a.id} variant="secondary" size="sm" onClick={() => nav(`/roster/artist/${a.id}`)} data-testid={`roster-artist-link-${a.id}`}>{a.name}</Button>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden" data-testid="roster-releases-card">
        <div className="border-b border-border p-4"><h3 className="font-heading text-lg font-semibold">Releases ({releases.length})</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Release Date</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {releases.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No releases.</TableCell></TableRow>}
            {releases.map((r) => (
              <TableRow key={r.id} data-testid={`roster-release-${r.id}`}>
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell>{r.release_type || "—"}</TableCell>
                <TableCell>{r.release_date || "—"}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="overflow-x-auto" data-testid="roster-royalties-card">
        <div className="border-b border-border p-4"><h3 className="font-heading text-lg font-semibold">Royalties ({royalties.length})</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Release</TableHead><TableHead>Track</TableHead><TableHead>DSP</TableHead><TableHead className="text-right">Streams</TableHead><TableHead className="text-right">Payable</TableHead></TableRow></TableHeader>
          <TableBody>
            {royalties.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No royalty records.</TableCell></TableRow>}
            {royalties.map((r) => (
              <TableRow key={r.id} data-testid={`roster-royalty-${r.id}`}>
                <TableCell>{r.period}</TableCell>
                <TableCell>{r.release || "—"}</TableCell>
                <TableCell>{r.track || "—"}</TableCell>
                <TableCell>{r.dsp}</TableCell>
                <TableCell className="text-right font-mono">{Number(r.streams || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-400">{money(r.payable_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
